// Estúdio de identidade da Teia, no modelo do Farol Studio: tokens escolhidos
// pelo usuário viram variáveis CSS em tempo de execução e valem para o app
// inteiro. A configuração fica no navegador e é aplicada antes do primeiro
// render em main.tsx.

export type StudioConfig = {
  palette: string;
  font: string;
  radius: string;
  density: string;
  buttons: "solido" | "contorno" | "suave" | "neon";
  glow: boolean;
  background: "aurora" | "pontos" | "grade" | "ondas" | "constelacao" | "nevoa" | "halo" | "neon" | "topografia" | "nenhum";
  header: "vidro" | "solido" | "flutuante";
  motion: "completo" | "sutil" | "desligado";
  texture: "nenhuma" | "ruido" | "malha" | "pontilhado";
  surface: "solida" | "vidro" | "tinta";
  badges: "ponto" | "suave" | "contorno";
};

export const STUDIO_KEY = "teia:studio";

/* --- Paletas: acento claro e escuro explícitos, contraste conferido.
   `duo*` é a segunda cor dos degradês (aurora, detalhes); sem ela, deriva do acento. */
export const STUDIO_PALETTES: Record<
  string,
  { nome: string; dica: string; light: string; lightStrong: string; dark: string; darkStrong: string; duoLight?: string; duoDark?: string }
> = {
  teia: { nome: "Teia", dica: "O verde original da casa", light: "#10a37f", lightStrong: "#0c8a6a", dark: "#22c99d", darkStrong: "#3adfb4", duoLight: "#8b5cf6", duoDark: "#8b5cf6" },
  quartzo: { nome: "Quartzo", dica: "Violeta com verde-água, a dupla do Tailark", light: "#6d6bf0", lightStrong: "#5654d6", dark: "#9b99fe", darkStrong: "#b6b5ff", duoLight: "#21b8a8", duoDark: "#2bc8b7" },
  farol: { nome: "Farol", dica: "Azul de farol com areia", light: "#1f5378", lightStrong: "#173f5c", dark: "#7fb3d5", darkStrong: "#a5c9e3", duoLight: "#c9a86a", duoDark: "#d9bd85" },
  cobalto: { nome: "Cobalto", dica: "Azul elétrico de produto", light: "#2f5bea", lightStrong: "#2348c4", dark: "#7c9bff", darkStrong: "#9fb5ff", duoLight: "#22d3ee", duoDark: "#67e8f9" },
  ultravioleta: { nome: "Ultravioleta", dica: "Violeta profundo", light: "#7c3aed", lightStrong: "#6428cc", dark: "#a78bfa", darkStrong: "#c4b0fc", duoLight: "#ec4899", duoDark: "#f472b6" },
  gelo: { nome: "Gelo", dica: "Verde-água sereno", light: "#0f766e", lightStrong: "#0b5b55", dark: "#5eead4", darkStrong: "#8cf1e1", duoLight: "#38bdf8", duoDark: "#7dd3fc" },
  brasa: { nome: "Brasa", dica: "Laranja queimado", light: "#dc5b21", lightStrong: "#b8481a", dark: "#fb923c", darkStrong: "#fcae66", duoLight: "#e11d48", duoDark: "#fb7185" },
  amanhecer: { nome: "Amanhecer", dica: "Rosa com âmbar, fim de madrugada", light: "#db2777", lightStrong: "#b91f64", dark: "#f472b6", darkStrong: "#f9a8d4", duoLight: "#f59e0b", duoDark: "#fbbf24" },
  vinho: { nome: "Vinho", dica: "Tinto sóbrio", light: "#9f1239", lightStrong: "#800e2e", dark: "#fb7185", darkStrong: "#fc93a2" },
  grafite: { nome: "Grafite", dica: "Monocromo total", light: "#3f4650", lightStrong: "#2b3138", dark: "#c3c9d2", darkStrong: "#dde1e7" },
};

/* --- Fontes: pares título/texto, carregados do Google Fonts sob demanda ---- */
export const STUDIO_FONTS: Record<
  string,
  { nome: string; dica: string; display: string; text: string; g: string[]; espaco: string }
> = {
  inter: {
    nome: "Inter",
    dica: "Neutra e precisa, padrão da Teia",
    display: '"Inter Variable", "Inter", system-ui, sans-serif',
    text: '"Inter Variable", "Inter", system-ui, sans-serif',
    g: [],
    espaco: "-0.028em",
  },
  espacial: {
    nome: "Espacial",
    dica: "Space Grotesk nos títulos",
    display: "'Space Grotesk', 'Inter Variable', system-ui, sans-serif",
    text: "'Inter Tight', 'Inter Variable', system-ui, sans-serif",
    g: ["Space+Grotesk:wght@400..700", "Inter+Tight:wght@300..700"],
    espaco: "-0.03em",
  },
  sinal: {
    nome: "Sinal",
    dica: "Sora com IBM Plex Sans",
    display: "'Sora', 'Inter Variable', system-ui, sans-serif",
    text: "'IBM Plex Sans', system-ui, sans-serif",
    g: ["Sora:wght@300..700", "IBM+Plex+Sans:wght@300;400;500;600"],
    espaco: "-0.025em",
  },
  amplo: {
    nome: "Amplo",
    dica: "Outfit com Manrope, formas abertas",
    display: "'Outfit', 'Inter Variable', system-ui, sans-serif",
    text: "'Manrope', system-ui, sans-serif",
    g: ["Outfit:wght@300..700", "Manrope:wght@300..700"],
    espaco: "-0.03em",
  },
  editorial: {
    nome: "Editorial",
    dica: "Instrument Sans, contraste sóbrio",
    display: "'Instrument Sans', 'Inter Variable', system-ui, sans-serif",
    text: "'Instrument Sans', system-ui, sans-serif",
    g: ["Instrument+Sans:wght@400..700"],
    espaco: "-0.026em",
  },
  denso: {
    nome: "Denso",
    dica: "Archivo, título com peso de manchete",
    display: "'Archivo', 'Inter Variable', system-ui, sans-serif",
    text: "'Archivo', system-ui, sans-serif",
    g: ["Archivo:wght@300..800"],
    espaco: "-0.032em",
  },
  suave: {
    nome: "Suave",
    dica: "Onest, curvas macias",
    display: "'Onest', 'Inter Variable', system-ui, sans-serif",
    text: "'Onest', system-ui, sans-serif",
    g: ["Onest:wght@300..800"],
    espaco: "-0.026em",
  },
  clinico: {
    nome: "Clínico",
    dica: "Plus Jakarta, leitura longa",
    display: "'Plus Jakarta Sans', 'Inter Variable', system-ui, sans-serif",
    text: "'Plus Jakarta Sans', system-ui, sans-serif",
    g: ["Plus+Jakarta+Sans:wght@300..700"],
    espaco: "-0.024em",
  },
};

/* --- Geometria e ritmo ------------------------------------------------------ */
export const STUDIO_RADII: Record<string, { nome: string; lg: number; md: number; sm: number }> = {
  reto: { nome: "Reto", lg: 4, md: 3, sm: 2 },
  sutil: { nome: "Sutil", lg: 10, md: 8, sm: 5 },
  suave: { nome: "Suave", lg: 16, md: 12, sm: 8 },
  redondo: { nome: "Redondo", lg: 22, md: 16, sm: 10 },
  capsula: { nome: "Cápsula", lg: 30, md: 22, sm: 14 },
};

export const STUDIO_DENSITIES: Record<string, { nome: string; dica: string; fontPx: number; pad: number }> = {
  compacta: { nome: "Compacta", dica: "Mais dados por tela", fontPx: 14, pad: 0.85 },
  equilibrada: { nome: "Equilibrada", dica: "O meio-termo da casa", fontPx: 15, pad: 1 },
  ampla: { nome: "Ampla", dica: "Respiro de apresentação", fontPx: 16, pad: 1.18 },
};

export const STUDIO_BUTTONS: Record<string, { nome: string; dica: string }> = {
  solido: { nome: "Sólido", dica: "Preenchido, direto" },
  contorno: { nome: "Contorno", dica: "Borda no acento" },
  suave: { nome: "Suave", dica: "Fundo translúcido" },
  neon: { nome: "Neon", dica: "Preenchido com brilho" },
};

/* --- Fundo animado da home (só CSS, nada de bibliotecas) -------------------- */
export const STUDIO_BACKGROUNDS: Record<StudioConfig["background"], { nome: string; dica: string }> = {
  aurora: { nome: "Aurora", dica: "Manchas de luz em deriva lenta" },
  pontos: { nome: "Pontos", dica: "Grade de pontos que respiram" },
  grade: { nome: "Grade", dica: "Linhas subindo, clima de terminal" },
  ondas: { nome: "Ondas", dica: "Faixas de seda em movimento" },
  constelacao: { nome: "Constelação", dica: "Estrelas piscando em três camadas" },
  nevoa: { nome: "Névoa", dica: "Bruma lenta atravessando a tela" },
  halo: { nome: "Halo", dica: "Um só brilho pulsando atrás da busca" },
  neon: { nome: "Neon", dica: "Malha luminosa em perspectiva" },
  topografia: { nome: "Topografia", dica: "Curvas de nível em deriva" },
  nenhum: { nome: "Nenhum", dica: "Fundo limpo, foco total" },
};

/* --- Estilo do menu superior ------------------------------------------------ */
export const STUDIO_HEADERS: Record<StudioConfig["header"], { nome: string; dica: string }> = {
  vidro: { nome: "Vidro", dica: "Translúcido com desfoque" },
  solido: { nome: "Sólido", dica: "Opaco, sem transparência" },
  flutuante: { nome: "Flutuante", dica: "Cápsula solta no topo" },
};

/* --- Quanto movimento a interface faz ---------------------------------------- */
export const STUDIO_MOTIONS: Record<StudioConfig["motion"], { nome: string; dica: string }> = {
  completo: { nome: "Completo", dica: "Fundos e cartões animados" },
  sutil: { nome: "Sutil", dica: "Movimento mais lento e discreto" },
  desligado: { nome: "Desligado", dica: "Sem animações decorativas" },
};

/* --- Textura da página (granulado por cima, no espírito dos fundos do Tailark) */
export const STUDIO_TEXTURES: Record<StudioConfig["texture"], { nome: string; dica: string }> = {
  nenhuma: { nome: "Nenhuma", dica: "Superfícies limpas" },
  ruido: { nome: "Ruído", dica: "Granulado fino de filme" },
  malha: { nome: "Malha", dica: "Malha quadriculada sutil ao fundo" },
  pontilhado: { nome: "Pontilhado", dica: "Matriz de pontos discreta ao fundo" },
};

/* --- Estilo dos selos de situação (Ativa, Baixada, máscara) ------------------ */
export const STUDIO_BADGES: Record<StudioConfig["badges"], { nome: string; dica: string }> = {
  ponto: { nome: "Ponto", dica: "Pílula neutra, cor só no ponto" },
  suave: { nome: "Suave", dica: "Fundo tingido na cor da situação" },
  contorno: { nome: "Contorno", dica: "Só a borda e o texto coloridos" },
};

/* --- Tratamento das superfícies (cartões, painéis) --------------------------- */
export const STUDIO_SURFACES: Record<StudioConfig["surface"], { nome: string; dica: string }> = {
  solida: { nome: "Sólida", dica: "Cartões opacos, contraste direto" },
  vidro: { nome: "Vidro", dica: "Cartões translúcidos com desfoque" },
  tinta: { nome: "Tinta", dica: "Preenchimento suave derivado do texto" },
};

/* O padrão da casa: grafite monocromo, cápsulas, topografia ao fundo e menu
   sólido — o mais sóbrio do conjunto, no espírito das interfaces da OpenAI. */
export const DEFAULT_STUDIO: StudioConfig = {
  palette: "grafite",
  font: "inter",
  radius: "capsula",
  density: "equilibrada",
  buttons: "solido",
  glow: true,
  background: "topografia",
  header: "solido",
  motion: "completo",
  texture: "nenhuma",
  surface: "solida",
  badges: "ponto",
};

/* --- Modelos: identidades completas, no espírito dos presets do Farol e do
   catálogo do Tailark. Os quatro primeiros são as combinações mais lapidadas. */
export const STUDIO_PRESETS: Record<string, { nome: string; familia: string; dica: string; config: StudioConfig }> = {
  casa: {
    nome: "Teia",
    familia: "A casa",
    dica: "O padrão: grafite, cápsulas e topografia ao fundo",
    config: { ...DEFAULT_STUDIO },
  },
  quartzo: {
    nome: "Quartzo",
    familia: "Catálogo Tailark",
    dica: "Violeta e verde-água, vidro, ruído de filme e menu flutuante",
    config: { palette: "quartzo", font: "espacial", radius: "suave", density: "equilibrada", buttons: "solido", glow: true, background: "aurora", header: "flutuante", motion: "completo", texture: "ruido", surface: "vidro", badges: "ponto" },
  },
  teia: {
    nome: "Verde original",
    familia: "Primeira fase",
    dica: "O verde da primeira versão, com aurora",
    config: { palette: "teia", font: "inter", radius: "suave", density: "equilibrada", buttons: "solido", glow: true, background: "aurora", header: "vidro", motion: "completo", texture: "nenhuma", surface: "solida", badges: "ponto" },
  },
  obsidiana: {
    nome: "Obsidiana",
    familia: "Pedra polida",
    dica: "Grafite com granulado, grade subindo e neon contido",
    config: { palette: "grafite", font: "denso", radius: "sutil", density: "compacta", buttons: "neon", glow: true, background: "grade", header: "solido", motion: "completo", texture: "ruido", surface: "solida", badges: "ponto" },
  },
  amanhecer: {
    nome: "Amanhecer",
    familia: "Primeira luz",
    dica: "Rosa e âmbar, ondas de seda, cartões de vidro",
    config: { palette: "amanhecer", font: "amplo", radius: "redondo", density: "ampla", buttons: "suave", glow: true, background: "ondas", header: "vidro", motion: "completo", texture: "nenhuma", surface: "vidro", badges: "ponto" },
  },
  circuito: {
    nome: "Circuito",
    familia: "Placa de bancada",
    dica: "Cobalto, malha quadriculada, cantos retos e tinta",
    config: { palette: "cobalto", font: "sinal", radius: "reto", density: "compacta", buttons: "contorno", glow: false, background: "pontos", header: "solido", motion: "sutil", texture: "malha", surface: "tinta", badges: "contorno" },
  },
  comando: {
    nome: "Comando",
    familia: "Sala de operações",
    dica: "Grafite, cantos retos, grade ao fundo",
    config: { palette: "grafite", font: "denso", radius: "reto", density: "compacta", buttons: "contorno", glow: false, background: "grade", header: "solido", motion: "sutil", texture: "nenhuma", surface: "solida", badges: "ponto" },
  },
  console: {
    nome: "Console",
    familia: "Terminal vivo",
    dica: "Azul elétrico, Space Grotesk e neon",
    config: { palette: "cobalto", font: "espacial", radius: "sutil", density: "compacta", buttons: "neon", glow: true, background: "pontos", header: "solido", motion: "completo", texture: "pontilhado", surface: "solida", badges: "ponto" },
  },
  radar: {
    nome: "Radar",
    familia: "Varredura calma",
    dica: "Verde-água, Sora e ondas suaves",
    config: { palette: "gelo", font: "sinal", radius: "redondo", density: "equilibrada", buttons: "suave", glow: true, background: "ondas", header: "vidro", motion: "completo", texture: "nenhuma", surface: "solida", badges: "ponto" },
  },
  manchete: {
    nome: "Manchete",
    familia: "Redação",
    dica: "Vinho sóbrio, tipografia editorial, zero ruído",
    config: { palette: "vinho", font: "editorial", radius: "reto", density: "ampla", buttons: "solido", glow: false, background: "nenhum", header: "solido", motion: "sutil", texture: "nenhuma", surface: "solida", badges: "ponto" },
  },
  varanda: {
    nome: "Varanda",
    familia: "Fim de tarde",
    dica: "Laranja queimado, cápsulas e menu flutuante",
    config: { palette: "brasa", font: "amplo", radius: "capsula", density: "ampla", buttons: "suave", glow: true, background: "ondas", header: "flutuante", motion: "completo", texture: "nenhuma", surface: "solida", badges: "suave" },
  },
  plataforma: {
    nome: "Plataforma",
    familia: "Produto sério",
    dica: "Azul de farol, Plus Jakarta, movimento contido",
    config: { palette: "farol", font: "clinico", radius: "suave", density: "equilibrada", buttons: "solido", glow: true, background: "aurora", header: "vidro", motion: "sutil", texture: "nenhuma", surface: "solida", badges: "ponto" },
  },
  madrugada: {
    nome: "Madrugada",
    familia: "Depois das 22h",
    dica: "Ultravioleta, pontos piscando e cápsula no topo",
    config: { palette: "ultravioleta", font: "espacial", radius: "redondo", density: "equilibrada", buttons: "neon", glow: true, background: "pontos", header: "flutuante", motion: "completo", texture: "ruido", surface: "vidro", badges: "suave" },
  },
};

/** Descobre qual modelo bate com a configuração atual (para destacar no Estúdio). */
export function matchPreset(config: StudioConfig): string | undefined {
  const keys = Object.keys(DEFAULT_STUDIO) as Array<keyof StudioConfig>;
  return Object.keys(STUDIO_PRESETS).find((id) =>
    keys.every((key) => STUDIO_PRESETS[id].config[key] === config[key]),
  );
}

export function loadStudio(): StudioConfig {
  try {
    const raw = localStorage.getItem(STUDIO_KEY);
    if (!raw) return DEFAULT_STUDIO;
    const parsed = JSON.parse(raw) as Partial<StudioConfig>;
    return {
      ...DEFAULT_STUDIO,
      ...parsed,
      palette: parsed.palette && STUDIO_PALETTES[parsed.palette] ? parsed.palette : DEFAULT_STUDIO.palette,
      font: parsed.font && STUDIO_FONTS[parsed.font] ? parsed.font : DEFAULT_STUDIO.font,
      radius: parsed.radius && STUDIO_RADII[parsed.radius] ? parsed.radius : DEFAULT_STUDIO.radius,
      density: parsed.density && STUDIO_DENSITIES[parsed.density] ? parsed.density : DEFAULT_STUDIO.density,
      buttons: parsed.buttons && STUDIO_BUTTONS[parsed.buttons] ? parsed.buttons : DEFAULT_STUDIO.buttons,
      background: parsed.background && STUDIO_BACKGROUNDS[parsed.background] ? parsed.background : DEFAULT_STUDIO.background,
      header: parsed.header && STUDIO_HEADERS[parsed.header] ? parsed.header : DEFAULT_STUDIO.header,
      motion: parsed.motion && STUDIO_MOTIONS[parsed.motion] ? parsed.motion : DEFAULT_STUDIO.motion,
      texture: parsed.texture && STUDIO_TEXTURES[parsed.texture] ? parsed.texture : DEFAULT_STUDIO.texture,
      surface: parsed.surface && STUDIO_SURFACES[parsed.surface] ? parsed.surface : DEFAULT_STUDIO.surface,
      badges: parsed.badges && STUDIO_BADGES[parsed.badges] ? parsed.badges : DEFAULT_STUDIO.badges,
    };
  } catch {
    return DEFAULT_STUDIO;
  }
}

export function saveStudio(config: StudioConfig): void {
  try {
    localStorage.setItem(STUDIO_KEY, JSON.stringify(config));
  } catch {
    // sem armazenamento; a identidade vale só até recarregar
  }
}

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  return [0, 2, 4].map((index) => parseInt(clean.slice(index, index + 2), 16)) as [number, number, number];
}

function rgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const FONT_LINK_ID = "teia-studio-fonts";

/** Aplica a configuração no documento inteiro (variáveis CSS + atributos). */
export function applyStudio(config: StudioConfig): void {
  const root = document.documentElement;
  const palette = STUDIO_PALETTES[config.palette] ?? STUDIO_PALETTES.teia;
  const font = STUDIO_FONTS[config.font] ?? STUDIO_FONTS.inter;
  const radius = STUDIO_RADII[config.radius] ?? STUDIO_RADII.suave;
  const density = STUDIO_DENSITIES[config.density] ?? STUDIO_DENSITIES.equilibrada;

  root.style.setProperty("--accent-light", palette.light);
  root.style.setProperty("--accent-light-strong", palette.lightStrong);
  root.style.setProperty("--accent-dark", palette.dark);
  root.style.setProperty("--accent-dark-strong", palette.darkStrong);
  root.style.setProperty("--accent-soft-light", rgba(palette.light, 0.1));
  root.style.setProperty("--accent-soft-dark", rgba(palette.dark, 0.13));
  root.style.setProperty("--ring-light", rgba(palette.light, 0.3));
  root.style.setProperty("--ring-dark", rgba(palette.dark, 0.35));
  root.style.setProperty("--accent2-light", palette.duoLight ?? palette.light);
  root.style.setProperty("--accent2-dark", palette.duoDark ?? palette.dark);

  root.style.setProperty("--radius-lg", `${radius.lg}px`);
  root.style.setProperty("--radius-md", `${radius.md}px`);
  root.style.setProperty("--radius-sm", `${radius.sm}px`);

  root.style.setProperty("--font-display", font.display);
  root.style.setProperty("--font-sans", font.text);
  root.style.setProperty("--display-tracking", font.espaco);

  root.style.setProperty("--base-font", `${density.fontPx}px`);
  root.style.setProperty("--pad-scale", String(density.pad));
  // Altura dos controles acompanha o ritmo escolhido, sem quebrar o alinhamento.
  root.style.setProperty("--control-h", `${Math.round(38 * (0.9 + density.pad * 0.1))}px`);
  root.style.setProperty("--control-h-sm", `${Math.round(32 * (0.9 + density.pad * 0.1))}px`);

  root.dataset.buttons = config.buttons;
  root.dataset.glow = config.glow ? "on" : "off";
  root.dataset.bg = config.background;
  root.dataset.header = config.header;
  root.dataset.motion = config.motion;
  root.dataset.texture = config.texture;
  root.dataset.surface = config.surface;
  root.dataset.badges = config.badges;

  // Fontes do Google só quando o par escolhido precisa delas.
  const existing = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (font.g.length) {
    const href = `https://fonts.googleapis.com/css2?family=${font.g.join("&family=")}&display=swap`;
    if (existing) {
      if (existing.href !== href) existing.href = href;
    } else {
      const link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    }
  } else if (existing) {
    existing.remove();
  }

  window.dispatchEvent(new CustomEvent("teia:studio", { detail: config }));
}

export function initStudio(): StudioConfig {
  const config = loadStudio();
  applyStudio(config);
  return config;
}
